import { useCallback, useMemo, useState } from 'react'
import type { SideData, SideDirection, SideState } from '../types'
import { MAP_SIZE } from '../constants'

/** 합쳐진 표시등 그룹 (뚱십: 2×5, 5×2 묶기용) */
export interface MergedGroup {
  id: string
  dir: SideDirection
  indices: number[]
  /** 풀기 시 done→on 복원을 위한 맵 영역 좌표 */
  mapRange?: { startCol: number; startRow: number; endCol: number; endRow: number }
}

function createSides(direction: SideDirection): SideData[] {
  return Array.from({ length: MAP_SIZE }, (_, i) => ({
    index: i,
    direction,
    state: 'off' as SideState,
    isMerged: false,
  }))
}

let mergeIdCounter = 0

export function useSideIndicators() {
  const [xSides, setXSides] = useState<SideData[]>(() => createSides('x'))
  const [ySides, setYSides] = useState<SideData[]>(() => createSides('y'))
  const [mergedGroups, setMergedGroups] = useState<MergedGroup[]>([])

  const getSetter = (dir: SideDirection) => (dir === 'x' ? setXSides : setYSides)

  /** 표시등 상태 변경 */
  const setSideState = useCallback((dir: SideDirection, index: number, state: SideState) => {
    const setter = getSetter(dir)
    setter(prev => prev.map((s, i) => (i === index ? { ...s, state } : s)))
  }, [])

  /** 줄십 묶기: 1×10 → y-side full, 10×1 → x-side full */
  const tieGroup = useCallback(
    (dir: SideDirection, index: number, count: number) => {
      if (count === 10) {
        setSideState(dir, index, 'full')
      }
    },
    [setSideState],
  )

  /** 뚱십 묶기: 2×5, 5×2 등 여러 열/행을 합쳐서 "십" 표시 */
  const tieMergedGroup = useCallback((
    dir: SideDirection,
    indices: number[],
    mapRange?: { startCol: number; startRow: number; endCol: number; endRow: number },
  ) => {
    const id = `merge-${++mergeIdCounter}`
    const setter = getSetter(dir)

    // 해당 인덱스들을 full + isMerged로 설정
    setter(prev => prev.map((s, i) => {
      if (indices.includes(i)) {
        return { ...s, state: 'full' as SideState, isMerged: true }
      }
      return s
    }))

    // mergedGroups에 추가
    setMergedGroups(prev => [...prev, { id, dir, indices, mapRange }])
  }, [])

  /** 뚱십 풀기: mergedGroup 제거 + 표시등 off 복원 */
  const releaseMergedGroup = useCallback((groupId: string, group: MergedGroup) => {
    // mergedGroups에서 제거
    setMergedGroups(prev => prev.filter(g => g.id !== groupId))

    // 표시등 상태 복원 (십 → 개별 번호)
    const setter = getSetter(group.dir)
    setter(prev => prev.map((s, i) => {
      if (group.indices.includes(i)) {
        return { ...s, state: 'off' as SideState, isMerged: false }
      }
      return s
    }))
  }, [])

  /** 표시등 해제 (full → off) */
  const releaseSide = useCallback((dir: SideDirection, index: number) => {
    const setter = getSetter(dir)
    setter(prev => {
      const current = prev[index]
      if (current.state === 'full') {
        return prev.map((s, i) => (i === index ? { ...s, state: 'off' as SideState, isMerged: false } : s))
      }
      return prev
    })
    // mergedGroups에서도 제거
    setMergedGroups(prev => prev.filter(g => !(g.dir === dir && g.indices.includes(index))))
  }, [])

  /** full 카운트 (십의 자리 합산). full 1개 = 10 */
  const fullCount = useMemo(() => {
    let tens = 0
    for (const side of [...xSides, ...ySides]) {
      if (side.state === 'full') tens += 10
    }
    return tens
  }, [xSides, ySides])

  /** 전체 리셋 */
  const resetSides = useCallback(() => {
    setXSides(createSides('x'))
    setYSides(createSides('y'))
    setMergedGroups([])
  }, [])

  return {
    xSides,
    ySides,
    mergedGroups,
    setSideState,
    tieGroup,
    tieMergedGroup,
    releaseSide,
    releaseMergedGroup,
    fullCount,
    resetSides,
  }
}
